import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';

// Baseline curated set — always available on the keyboard even if the API is
// down. Any emoji the user pastes/saves is appended to this and persisted on
// the backend as a shared workspace favourites list.
export const BASELINE_EMOJIS = [
  '🩷','💕','☀️','🫧','✨','🌟','💫','☄️','🥰','☺️','🤩','🙌','👩‍💻',
  '🎨','🖼️','📲','🤳','☎️','💄','💋','🧴','🍑','🍓',
  '🍏','🥥','🍍','🍇','🥭','🫐','🗽','🏝️','🍦','☕️',
  '💛','❤️','🩵','💙','🤍','🩶',
  '🧖‍♀️','🚿','🔥','⚡️','🔧','🍝','🪐','🚀','🪩','🍾','🧨','🕯️','🎂','🧁',
];

const EmojiContext = createContext({
  emojis: BASELINE_EMOJIS,
  saveEmoji: async () => {},
});

export const EmojiProvider = ({ children }) => {
  const [custom, setCustom] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/emoji-favourites');
        setCustom(Array.isArray(data?.emojis) ? data.emojis : []);
      } catch { /* not signed in yet or endpoint unreachable — ignore */ }
    })();
  }, []);

  const saveEmoji = useCallback(async (emoji) => {
    const clean = (emoji || '').trim();
    if (!clean) return;
    // Optimistically add locally (deduped against baseline + custom)
    setCustom((prev) => {
      const combined = new Set([...BASELINE_EMOJIS, ...prev]);
      if (combined.has(clean)) return prev;
      return [...prev, clean];
    });
    try {
      const { data } = await api.post('/emoji-favourites', { emoji: clean });
      if (Array.isArray(data?.emojis)) setCustom(data.emojis);
    } catch { /* keep optimistic addition */ }
  }, []);

  // Merged deduped list: baseline first, then any newly saved customs.
  const seen = new Set();
  const emojis = [...BASELINE_EMOJIS, ...custom].filter((e) => {
    if (seen.has(e)) return false;
    seen.add(e); return true;
  });

  return (
    <EmojiContext.Provider value={{ emojis, saveEmoji }}>
      {children}
    </EmojiContext.Provider>
  );
};

export const useEmojis = () => useContext(EmojiContext);

// Back-compat named export so existing imports of `FAVOURITE_EMOJIS` still work
// when the context isn't available (e.g. static references).
export const FAVOURITE_EMOJIS = BASELINE_EMOJIS;
